/**
 * profileLoader.ts
 *
 * A dataloader-style batcher for kind-0 profile metadata.
 *
 * Feeds render dozens of post cards at once, and every card needs its
 * author's profile. Without batching this means one relay query per card
 * (N+1), each fanned out to every relay in the pool — on mobile that
 * saturates the network and many profiles never resolve.
 *
 * Instead, profile requests made within a short window are coalesced into a
 * single `kinds: [0]` query with all requested authors, and the results are
 * distributed back to the individual callers.
 */

import type { NostrEvent, NPool } from '@nostrify/nostrify';

/** How long to collect requests before firing a batch. */
const BATCH_WINDOW_MS = 40;
/** Max authors per relay query. */
const CHUNK_SIZE = 200;
/** Timeout for a batched profile query. */
const QUERY_TIMEOUT_MS = 6000;

interface Waiter {
  resolve: (event: NostrEvent | undefined) => void;
  reject: (err: unknown) => void;
  cleanup: () => void;
}

/** pubkey → waiters queued for the next batch */
let queue = new Map<string, Waiter[]>();
/** The pool instance to query with (captured from the latest caller). */
let poolRef: NPool | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * Request a profile event for a pubkey. Resolves with the latest kind-0
 * event, or undefined if none was found. The underlying relay query is
 * shared with all other requests made within the batch window.
 *
 * The caller's signal only cancels *its own* wait — the shared batch query
 * continues for the other waiters, so one unmounting card can't break the
 * profile loads of the rest of the feed.
 */
export function loadProfile(
  nostr: NPool,
  pubkey: string,
  signal?: AbortSignal,
): Promise<NostrEvent | undefined> {
  if (!pubkey) return Promise.resolve(undefined);

  return new Promise<NostrEvent | undefined>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? abortError());
      return;
    }

    let onAbort: (() => void) | undefined;
    const waiter: Waiter = {
      resolve,
      reject,
      cleanup: () => {
        if (onAbort && signal) signal.removeEventListener('abort', onAbort);
      },
    };

    if (signal) {
      onAbort = () => {
        // Drop this waiter; the batch keeps running for everyone else.
        const waiters = queue.get(pubkey);
        if (waiters) {
          const idx = waiters.indexOf(waiter);
          if (idx >= 0) waiters.splice(idx, 1);
          if (waiters.length === 0) queue.delete(pubkey);
        }
        reject(signal.reason ?? abortError());
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }

    let waiters = queue.get(pubkey);
    if (!waiters) {
      waiters = [];
      queue.set(pubkey, waiters);
    }
    waiters.push(waiter);
    poolRef = nostr;

    if (timer === null) {
      timer = setTimeout(flush, BATCH_WINDOW_MS);
    }
  });
}

function settleWaiters(waiters: Waiter[] | undefined, event: NostrEvent | undefined) {
  if (!waiters) return;
  for (const w of waiters) {
    w.cleanup();
    w.resolve(event);
  }
}

function rejectWaiters(waiters: Waiter[] | undefined, err: unknown) {
  if (!waiters) return;
  for (const w of waiters) {
    w.cleanup();
    w.reject(err);
  }
}

async function flush(): Promise<void> {
  timer = null;

  const batch = queue;
  queue = new Map();

  if (batch.size === 0) return;

  const nostr = poolRef;
  if (!nostr) {
    for (const waiters of batch.values()) {
      rejectWaiters(waiters, new Error('No relay pool available'));
    }
    return;
  }

  const pubkeys = [...batch.keys()];

  for (let i = 0; i < pubkeys.length; i += CHUNK_SIZE) {
    const chunk = pubkeys.slice(i, i + CHUNK_SIZE);
    try {
      const events = await nostr.query(
        [{ kinds: [0], authors: chunk, limit: chunk.length }],
        { signal: AbortSignal.timeout(QUERY_TIMEOUT_MS) },
      );

      // Keep the newest metadata per pubkey (profiles are replaceable, but
      // some relays return stale copies).
      const byPubkey = new Map<string, NostrEvent>();
      for (const ev of events) {
        const existing = byPubkey.get(ev.pubkey);
        if (!existing || ev.created_at > existing.created_at) {
          byPubkey.set(ev.pubkey, ev);
        }
      }

      for (const pk of chunk) {
        settleWaiters(batch.get(pk), byPubkey.get(pk));
      }
    } catch (err) {
      for (const pk of chunk) {
        rejectWaiters(batch.get(pk), err);
      }
    }
  }
}
